import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Trash2, Eye, Database, Wifi, WifiOff } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface SavedDocument {
  id: string;
  content: string;
  date: string;
  title: string;
  user_id?: string;
}

interface SavedDocumentsProps {
  onSelectDocument: (content: string) => void;
}

const SavedDocuments: React.FC<SavedDocumentsProps> = ({ onSelectDocument }) => {
  const [documents, setDocuments] = useState<SavedDocument[]>([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [storageType, setStorageType] = useState<'supabase' | 'localStorage' | 'indexedDB' | 'sample'>('localStorage');
  const { language, t } = useLanguage();
  const { user } = useAuth();

  // Update online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncDocuments();
    };
    
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize IndexedDB
  const initIndexedDB = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) {
        reject(new Error('IndexedDB not supported'));
        return;
      }
      
      const request = window.indexedDB.open('adaaltDocs', 1);
      
      request.onupgradeneeded = (event) => {
        const db = request.result;
        if (!db.objectStoreNames.contains('documents')) {
          const store = db.createObjectStore('documents', { keyPath: 'id' });
          store.createIndex('date', 'date', { unique: false });
        }
      };
      
      request.onsuccess = () => {
        resolve(request.result);
      };
      
      request.onerror = (event) => {
        reject(event);
      };
    });
  };

  // Get documents from IndexedDB
  const getDocumentsFromIndexedDB = async (): Promise<SavedDocument[]> => {
    try {
      const db = await initIndexedDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(['documents'], 'readonly');
        const store = transaction.objectStore('documents');
        const request = store.getAll();
        
        request.onsuccess = () => {
          resolve(request.result);
        };
        
        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error getting documents from IndexedDB:', error);
      return [];
    }
  };

  // Save documents to IndexedDB
  const saveDocumentsToIndexedDB = async (docs: SavedDocument[]): Promise<boolean> => {
    try {
      const db = await initIndexedDB();
      const transaction = db.transaction(['documents'], 'readwrite');
      const store = transaction.objectStore('documents');
      
      // Clear current store
      store.clear();
      
      // Add all documents
      docs.forEach(doc => {
        store.add(doc);
      });
      
      return true;
    } catch (error) {
      console.error('Error saving documents to IndexedDB:', error);
      return false;
    }
  };

  // Get documents from Supabase
  const getDocumentsFromSupabase = async (): Promise<SavedDocument[]> => {
    try {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('saved_documents')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      return data.map(doc => ({
        id: doc.id,
        content: doc.content,
        title: doc.title,
        date: doc.date
      })) || [];
    } catch (error) {
      console.error('Error getting documents from Supabase:', error);
      return [];
    }
  };

  // Save document to Supabase
  const saveDocumentToSupabase = async (doc: SavedDocument): Promise<boolean> => {
    try {
      if (!user) return false;
      
      const { error } = await supabase
        .from('saved_documents')
        .insert({
          id: doc.id,
          title: doc.title,
          content: doc.content,
          date: doc.date,
          user_id: user.id
        });
        
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error saving document to Supabase:', error);
      return false;
    }
  };

  // Delete document from Supabase
  const deleteDocumentFromSupabase = async (id: string): Promise<boolean> => {
    try {
      if (!user) return false;
      
      const { error } = await supabase
        .from('saved_documents')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting document from Supabase:', error);
      return false;
    }
  };

  const syncDocuments = async () => {
    if (!isOnline || !user) return;
    
    try {
      // Get documents from localStorage and IndexedDB
      const localStorageDocs = JSON.parse(localStorage.getItem('adaaltBotDocs') || '[]');
      const indexedDBDocs = await getDocumentsFromIndexedDB();
      
      // Get documents from Supabase
      const supabaseDocs = await getDocumentsFromSupabase();
      
      // Merge all documents (prefer newer versions if duplicates)
      const mergedDocs: { [id: string]: SavedDocument } = {};
      
      [...indexedDBDocs, ...localStorageDocs, ...supabaseDocs].forEach(doc => {
        // If we already have this doc, keep the newer one based on date
        if (mergedDocs[doc.id] && new Date(mergedDocs[doc.id].date) > new Date(doc.date)) {
          return;
        }
        mergedDocs[doc.id] = doc;
      });
      
      const finalDocs = Object.values(mergedDocs);
      
      // Update all storage
      localStorage.setItem('adaaltBotDocs', JSON.stringify(finalDocs));
      await saveDocumentsToIndexedDB(finalDocs);
      
      // Sync each document to Supabase if it's not already there
      if (user) {
        for (const doc of finalDocs) {
          if (!supabaseDocs.some(sDoc => sDoc.id === doc.id)) {
            await saveDocumentToSupabase(doc);
          }
        }
      }
      
      setDocuments(finalDocs);
      setStorageType('supabase');
    } catch (error) {
      console.error('Error syncing documents:', error);
    }
  };

  useEffect(() => {
    // Load saved documents when component mounts
    const loadDocuments = async () => {
      try {
        // If user is logged in and online, try Supabase first
        if (user && navigator.onLine) {
          const supabaseDocs = await getDocumentsFromSupabase();
          if (supabaseDocs.length > 0) {
            setDocuments(supabaseDocs);
            setStorageType('supabase');
            console.log('Documents loaded from Supabase');
            
            // Update local storage with this data for offline use
            localStorage.setItem('adaaltBotDocs', JSON.stringify(supabaseDocs));
            await saveDocumentsToIndexedDB(supabaseDocs);
            return;
          }
        }
        
        // If no Supabase data or offline, try IndexedDB
        const indexedDBDocs = await getDocumentsFromIndexedDB();
        if (indexedDBDocs.length > 0) {
          setDocuments(indexedDBDocs);
          setStorageType('indexedDB');
          console.log('Documents loaded from IndexedDB');
          return;
        }
        
        // Last resort: localStorage
        const savedDocs = JSON.parse(localStorage.getItem('adaaltBotDocs') || '[]');
        setDocuments(savedDocs);
        setStorageType('localStorage');
        console.log('Documents loaded from localStorage');
        
        // Save to IndexedDB for future use
        if (savedDocs.length > 0) {
          await saveDocumentsToIndexedDB(savedDocs);
        }
        
        // Sync with Supabase if online and logged in
        if (user && navigator.onLine && savedDocs.length > 0) {
          for (const doc of savedDocs) {
            await saveDocumentToSupabase(doc);
          }
        }
      } catch (error) {
        console.error("Error loading documents:", error);
        toast.error(t('message.error'));
        
        // Last resort: localStorage
        try {
          const savedDocs = JSON.parse(localStorage.getItem('adaaltBotDocs') || '[]');
          setDocuments(savedDocs);
          setStorageType('localStorage');
        } catch {
          // If all else fails, show empty state
          setDocuments([]);
        }
      }
    };

    loadDocuments();
    
    // Add event listener for storage changes (if user has multiple tabs open)
    window.addEventListener('storage', loadDocuments);
    
    return () => {
      window.removeEventListener('storage', loadDocuments);
    };
  }, [t, user]);

  const deleteDocument = async (id: string) => {
    try {
      // Filter out the deleted document
      const updatedDocs = documents.filter(doc => doc.id !== id);
      
      // Update state
      setDocuments(updatedDocs);
      
      // Update localStorage
      localStorage.setItem('adaaltBotDocs', JSON.stringify(updatedDocs));
      
      // Update IndexedDB
      await saveDocumentsToIndexedDB(updatedDocs);
      
      // Delete from Supabase if online and logged in
      if (user && navigator.onLine) {
        await deleteDocumentFromSupabase(id);
      }
      
      toast.success(t('message.deleted'));
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error(t('message.error'));
    }
  };

  const loadDocument = (content: string) => {
    onSelectDocument(content);
    toast.success(t('message.loaded'));
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'ur' ? 'ur-PK' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getStorageTypeLabel = () => {
    switch (storageType) {
      case 'supabase': return language === 'ur' ? 'سوپابیس' : 'Supabase';
      case 'indexedDB': return language === 'ur' ? 'مقامی ڈیٹابیس' : 'Local Database';
      case 'localStorage': return language === 'ur' ? 'مقامی ذخیرہ' : 'Local Storage';
      case 'sample': return language === 'ur' ? 'نمونہ ڈیٹا' : 'Sample Data';
      default: '';
    }
  };

  if (documents.length === 0) {
    return (
      <Card className="mt-6 p-6 text-center">
        <CardTitle className="text-lg mb-2">{t('saved.empty')}</CardTitle>
        <p>{language === 'ur' ? 'آپ کی محفوظ کردہ دستاویزات یہاں دکھائی جائیں گی' : 'Your saved documents will appear here'}</p>
      </Card>
    );
  }

  return (
    <Card className="mt-6">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className={`text-lg ${language === 'ur' ? "text-right" : "text-left"}`}>
          {t('saved.title')}
        </CardTitle>
        <div className="flex items-center text-xs text-gray-500">
          <Database size={14} className="mr-1" />
          {getStorageTypeLabel()}
          
          {isOnline ? (
            <div className="flex items-center text-green-600 ml-2">
              <Wifi size={14} className="mr-1" />
              {language === 'ur' ? 'آن لائن' : 'Online'}
            </div>
          ) : (
            <div className="flex items-center text-orange-500 ml-2">
              <WifiOff size={14} className="mr-1" />
              {language === 'ur' ? 'آف لائن' : 'Offline'}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {documents.map((doc) => (
          <div 
            key={doc.id}
            className="p-3 border rounded-md flex justify-between items-center"
            dir={language === 'ur' ? 'rtl' : 'ltr'}
          >
            <div className="flex gap-2">
              <Button
                onClick={() => loadDocument(doc.content)}
                variant="ghost"
                size="sm"
                className="flex items-center gap-1"
              >
                <Eye size={16} />
                {t('saved.view')}
              </Button>
              <Button
                onClick={() => deleteDocument(doc.id)}
                variant="outline"
                size="sm"
                className="flex items-center gap-1 text-red-500 hover:bg-red-50"
              >
                <Trash2 size={16} />
                {t('saved.delete')}
              </Button>
            </div>
            <div className={language === 'ur' ? "text-right" : "text-left"}>
              <h4 className="font-medium text-sm">{doc.title}</h4>
              <p className="text-xs text-muted-foreground">{formatDate(doc.date)}</p>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default SavedDocuments;
